import { Link, useNavigate } from "react-router-dom"
import { useState } from "react"
import { MapPin, Search, Building2, Stethoscope, ScanLine, X } from "lucide-react"

const locations = [
  { id: "madinaguda", name: "Madinaguda" },
  { id: "nallagandla", name: "Nallagandla" },
  { id: "isnapur", name: "Isnapur" },
  { id: "zaheerabad", name: "Zaheerabad" },
]

const specialities = [
  { id: "cardiology", name: "Cardiology", keywords: ["heart", "chest pain", "cardiac", "blood pressure"] },
  { id: "neurology", name: "Neurology", keywords: ["brain", "headache", "migraine", "stroke"] },
  { id: "orthopedics", name: "Orthopedics", keywords: ["bone", "joint pain", "fracture", "arthritis"] },
  { id: "paediatrics", name: "Paediatrics", keywords: ["child", "baby", "infant", "kids"] },
  { id: "gynaecology", name: "Obstetrics & Gynaecology", keywords: ["pregnancy", "women", "menstrual"] },
  { id: "general-medicine", name: "General Medicine", keywords: ["fever", "cold", "cough", "diabetes"] },
  { id: "gastroenterology", name: "Gastroenterology", keywords: ["stomach", "digestion", "acidity"] },
  { id: "pulmonology", name: "Pulmonology", keywords: ["lung", "breathing", "asthma"] },
  { id: "dermatology", name: "Dermatology", keywords: ["skin", "hair", "acne"] },
  { id: "ent", name: "ENT", keywords: ["ear", "nose", "throat"] },
]

export default function Herosectionwidget() {
  const navigate = useNavigate()
  const [selectedLocation, setSelectedLocation] = useState("")
  const [searchQuery, setSearchQuery] = useState("")
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)
  
  const searchSuggestedSpecialities = searchQuery
    ? specialities.filter(spec => {
        const query = searchQuery.toLowerCase()
        return spec.name.toLowerCase().includes(query) || spec.keywords?.some(k => k.toLowerCase().includes(query))
      })
    : specialities

  const buildBookNowUrl = () => {
    const params = new URLSearchParams()
    if (selectedLocation) params.append('location', selectedLocation)
    if (searchQuery) params.append('search', searchQuery)
    return `/book-now${params.toString() ? '?' + params.toString() : ''}`
  }

  const handleBookAppointment = () => {
    navigate(buildBookNowUrl())
  }

  return (
    <>
      {/* Desktop/Tablet */}
      <section className="hidden sm:block relative w-full overflow-hidden">
        <picture>
          <img src="/images/DoctorDesktop.webp" alt="Pranaam Hospitals Banner" className="w-full h-full object-contain" />
        </picture>
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 md:px-8">
            <div className="max-w-2xl">
              {/* Search */}
              <div className="bg-white rounded-lg shadow-xl p-3 flex flex-col sm:flex-row gap-2 max-w-3xl mb-6 relative">
                <div className="flex items-center gap-2 px-4 py-3 border-r min-w-[180px]">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className="border-none outline-none bg-transparent text-sm font-medium cursor-pointer w-full">
                    <option value="">All Locations</option>
                    {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                  </select>
                </div>
                <div className="flex-1 relative">
                  <div className="flex items-center gap-2 px-4 py-3">
                    <Search className="w-5 h-5 text-gray-400" />
                    <input 
                      type="text" 
                      placeholder="Search for Doctor or Speciality" 
                      value={searchQuery} 
                      onChange={(e) => {
                        setSearchQuery(e.target.value)
                        setShowSearchDropdown(true)
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      className="border-none outline-none w-full text-sm" 
                    />
                    {searchQuery && (
                      <button onClick={() => { setSearchQuery(""); setShowSearchDropdown(false) }} className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300">
                        <X className="w-4 h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                  {showSearchDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border max-h-80 overflow-y-auto z-50">
                      {searchSuggestedSpecialities.length > 0 && (
                        <div>
                          <p className="px-4 py-2 text-xs font-semibold text-gray-500 bg-gray-50">Specialities</p>
                          {searchSuggestedSpecialities.slice(0, 5).map(spec => (
                            <Link
                              key={spec.id}
                              to={`/book-now?specialty=${spec.id}`}
                              onClick={() => setShowSearchDropdown(false)}
                              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                            >
                              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#a52a2a1a'}}>
                                <Stethoscope className="w-5 h-5" style={{color: '#a52a2a'}} />
                              </div>
                              <span className="font-medium text-gray-800">{spec.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                      {searchQuery && searchSuggestedSpecialities.length === 0 && (
                        <p className="px-4 py-4 text-gray-500 text-center">No results found</p>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={handleBookAppointment} className="px-6 py-3 text-white font-medium rounded-md text-sm whitespace-nowrap" style={{backgroundColor: '#a52a2a'}}>
                  Book an Appointment
                </button>
              </div>
              {showSearchDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowSearchDropdown(false)} />}
              
              {/* Cards */}
              <div className="grid grid-cols-3 gap-4 max-w-2xl">
                <Link to="/book-now" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book Hospital</h3>
                  <p className="text-sm text-gray-600">Visit</p>
                </Link>
                <Link to="/book-now" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <Stethoscope className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book Health</h3>
                  <p className="text-sm text-gray-600">Checkup</p>
                </Link>
                <Link to="/book-now" className="rounded-xl p-6 text-center hover:shadow-xl transition-shadow" style={{backgroundColor: '#a52a2a1a'}}>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
                    <ScanLine className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Book</h3>
                  <p className="text-sm text-gray-600">Scans</p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile */}
      <section className="sm:hidden relative w-full">
        <img src="/images/Doctormobile.webp" alt="Pranaam Hospitals Banner" className="w-full h-auto" />
        <div className="absolute bottom-0 left-0 right-0 bg-white px-4">
           <div className="space-y-2 relative py-4">
            <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
              <MapPin className="w-4 h-4 text-gray-400" />
              <select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)} className="border-none outline-none bg-transparent text-sm flex-1">
                <option value="">All Locations</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select>
            </div>
            <div className="relative">
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
                <Search className="w-4 h-4 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search for Doctor or Speciality" 
                  value={searchQuery} 
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setShowSearchDropdown(true)
                  }}
                  onFocus={() => setShowSearchDropdown(true)}
                  className="border-none outline-none w-full text-sm" 
                />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(""); setShowSearchDropdown(false) }} className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>
              {showSearchDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border max-h-60 overflow-y-auto z-50">
                  {searchSuggestedSpecialities.length > 0 && (
                    <div>
                      <p className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50">Specialities</p>
                      {searchSuggestedSpecialities.slice(0, 5).map(spec => (
                        <Link
                          key={spec.id}
                          to={`/book-now?specialty=${spec.id}`}
                          onClick={() => setShowSearchDropdown(false)}
                          className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{backgroundColor: '#a52a2a1a'}}>
                            <Stethoscope className="w-4 h-4" style={{color: '#a52a2a'}} />
                          </div>
                          <span className="font-medium text-gray-800 text-sm">{spec.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                  {searchQuery && searchSuggestedSpecialities.length === 0 && (
                    <p className="px-3 py-3 text-gray-500 text-center text-sm">No results found</p>
                  )}
                </div>
              )}
            </div>
            <button onClick={handleBookAppointment} className="block w-full px-4 py-3 text-white font-medium rounded-lg text-center text-sm" style={{backgroundColor: '#a52a2a'}}>
              Book an Appointment
            </button>
            {showSearchDropdown && <div className="fixed inset-0 z-40" onClick={() => setShowSearchDropdown(false)} />}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book Hospital</p>
              <p className="text-xs text-gray-600">Visit</p>
            </Link>
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book Health</p>
              <p className="text-xs text-gray-600">Checkup</p>
            </Link>
            <Link to="/book-now" className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-50 flex items-center justify-center">
                <ScanLine className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-xs font-semibold">Book</p>
              <p className="text-xs text-gray-600">Scans</p>
            </Link>
          </div>
         
        </div>
      </section>
    </>
  );
}
